import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, GitBranch } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingState, ErrorState } from "@/components/ui/empty";
import {
  StatusBadge,
  KindBadge,
  EntryTypeBadge,
} from "@/components/status-badges";
import { useApi } from "@/lib/api";
import {
  formatDate,
  formatFitness,
  formatRelative,
  parseJson,
  shortHash,
  shortId,
} from "@/lib/format";
import type { Experiment, JournalResponse, Rung } from "@/lib/types";

export function ExperimentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const experiment = useApi<Experiment>(id ? `/api/experiments/${id}` : null);
  const journal = useApi<JournalResponse>("/api/journal");
  const rung = useApi<Rung>(
    experiment.data ? `/api/rungs/${experiment.data.rung_id}` : null,
  );

  const related = useMemo(
    () =>
      (journal.data?.entries ?? []).filter((e) => e.experiment_id === id),
    [journal.data, id],
  );

  if (experiment.loading) return <LoadingState />;
  if (experiment.error) return <ErrorState error={experiment.error} />;
  if (!experiment.data) return null;
  const e = experiment.data;
  const config = parseJson<Record<string, unknown>>(e.config, {});
  const passed =
    e.final_fitness !== null &&
    rung.data &&
    e.final_fitness >= rung.data.fitness_threshold;

  return (
    <>
      <Link
        to="/experiments"
        className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        All experiments
      </Link>
      <PageHeader
        title={e.name}
        description={
          e.description ??
          `${e.kind} run · started ${formatRelative(e.started_at)}`
        }
        actions={
          <div className="flex items-center gap-2">
            <KindBadge kind={e.kind} />
            <StatusBadge status={e.status} />
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Headline fitness */}
          <Card
            className={
              passed
                ? "border-emerald-500/30"
                : e.status === "failed"
                  ? "border-rose-500/30"
                  : ""
            }
          >
            <CardHeader className="pb-3">
              <CardDescription className="text-[10px] uppercase tracking-wider">
                Final fitness
              </CardDescription>
              <div className="flex items-baseline gap-3">
                <span
                  className={`text-4xl font-semibold tabular-nums ${passed ? "text-emerald-300" : e.final_fitness !== null ? "text-amber-300" : "text-muted-foreground"}`}
                >
                  {formatFitness(e.final_fitness)}
                </span>
                {rung.data && (
                  <span className="text-sm text-muted-foreground">
                    / threshold {rung.data.fitness_threshold.toFixed(3)}
                  </span>
                )}
                {passed && (
                  <Badge variant="success" className="ml-2">
                    PASS
                  </Badge>
                )}
              </div>
            </CardHeader>
          </Card>

          {/* Config */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                Config
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs md:grid-cols-3">
                {Object.entries(config)
                  .filter(([k]) => k !== "config")
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {k}
                      </dt>
                      <dd className="font-mono">
                        {typeof v === "object"
                          ? JSON.stringify(v)
                          : String(v)}
                      </dd>
                    </div>
                  ))}
              </dl>
            </CardContent>
          </Card>

          {/* Notes */}
          {e.notes && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                {e.notes}
              </CardContent>
            </Card>
          )}

          {/* Related journal entries */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Journal ({related.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {related.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No journal entries linked to this experiment.
                </p>
              ) : (
                related.map((j) => (
                  <Link
                    key={j.id}
                    to={`/journal/${j.id}`}
                    className="block space-y-1 rounded-md px-2 py-2 hover:bg-accent"
                  >
                    <div className="flex items-center gap-2">
                      <EntryTypeBadge entryType={j.entry_type} />
                      <span className="text-[10px] text-muted-foreground">
                        {formatRelative(j.timestamp)}
                      </span>
                    </div>
                    <div className="text-xs font-medium leading-snug">
                      {j.title}
                    </div>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <GitBranch className="h-4 w-4" />
                Provenance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <Meta label="Experiment ID" value={shortId(e.id)} mono />
              <Meta label="Kind" value={e.kind} mono />
              <Meta label="Rung" value={e.rung_id} mono />
              <Meta label="Engine" value={e.engine_version} mono />
              <Meta label="Git hash" value={shortHash(e.engine_git_hash)} mono />
              <Meta label="Runner" value={e.runner_id ?? "—"} mono />
              {e.campaign_id && (
                <div className="pt-2">
                  <Link
                    to={`/campaigns/${e.campaign_id}`}
                    className="text-primary hover:underline"
                  >
                    → View campaign
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Timing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <Meta label="Started" value={formatDate(e.started_at)} />
              <Meta
                label="Completed"
                value={e.completed_at ? formatDate(e.completed_at) : "—"}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

function Meta({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono text-right" : "text-right"}>
        {value}
      </span>
    </div>
  );
}
